<template>
  <div class="hyp-container-home">
    <header>
      <LeftSidePanel @minimized="handleMinimizedLeftSidePanel"></LeftSidePanel>
    </header>
    <main>
      <div class="hyp-home-right-container">
        <DetailsSidebar
          :left-panel-minimized="leftPanelMinimized"
        ></DetailsSidebar>
      </div>

      <Notifier></Notifier>
      <ExperimentalWarning />
      <NotificationsModal></NotificationsModal>
      <WelcomeMessage :forGrd="true"></WelcomeMessage>
    </main>
    <footer v-if="isWhiteLabelUserComputed">
      <div class="hyp-footer">Powered by Seerist Inc.</div>
    </footer>
  </div>
</template>

<script>
import lodashGet from "lodash/get";

import { mapGetters } from "vuex";

import {
  GET_STABILITY_REFRESH_MAP,
  GET_POWER_OUTAGE_REFRESH_MAP,
  GET_STABILITY_PARAMS,
  GET_HOTSPOTS_REFRESH_MAP,
  GET_TOPICS_V2_REFRESH_MAP,
  GET_TOPICS_V2_MAP_TOPIC,
  GET_NEWS_REFRESH_MAP,
  GET_SOCIAL_REFRESH_MAP,
  GET_MAP_REFRESH_DATA,
  GET_PLACES_REFRESH_MAP,
  GET_DAY_NIGHT_OVERLAY_REFRESH_MAP,
  GET_SELECTED_DATES,
  GET_SELECTOR_PANEL,
  GET_PREFERENCES,
  GET_WAITING_ON_WELCOME_MESSAGE,
  GET_SEARCH_LOCATION_REFRESH_MAP,
} from "@/store/actions.type";
import {
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_GRIDS,
  MODULE_PREFIX_HOTSPOTS,
  MODULE_PREFIX_TOPICS_V2,
  MODULE_PREFIX_NEWS,
  MODULE_PREFIX_SOCIAL,
  MODULE_PREFIX_USER,
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_SEARCH,
  MODULE_PREFIX_PLACES,
  MODULE_PREFIX_DAY_NIGHT_OVERLAY,
  MODULE_PREFIX_POWER_OUTAGE,
} from "@/store";
import {
  getLevelsForInitialLoad as getStabilityLevelsForInitialLoad,
  getLevelsFromPreferences as getStabilityLevelsFromPreferences,
} from "@/services/grids/stability.service";
import {
  getTopicsArray,
  getInfluencesArray,
} from "@/services/events/topics.service";
import { getDefaultStartEndDates } from "@/common/date.utilities";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { GRIDS_STABILITY_TYPES } from "@/common/constants/grids";
import {
  hasWatchOnlyAccess,
  hasSearchOnlyAccess,
  isWhiteLabelUser,
  getPreferredLandingPage,
} from "@/services/user/user.service";
import { pageTrackerLogger } from "@/common/pageTracker";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import { setTopicIcons } from "@/common/constants/icons";
import { setTopicIconsSVG } from "@/common/constants/topic_icons_svg";
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  setTopicEventCategories,
} from "@/common/constants/analytics";
import { setTopicDetailSidebarTypes } from "@/common/constants/home";
import { setTopicsIcons } from "@/common/constants/maps";
import { setLabels } from "@/common/constants/labels";

export default {
  name: "Home",
  components: {
    LeftSidePanel: () => import("@/components/common/LeftSidePanel"),
    DetailsSidebar: () => import("@/components/common/details/DetailsSidebar"),
    Notifier: () => import("@/components/common/Notifier"),
    ExperimentalWarning: () => import("@/components/auth/ExperimentalWarning"),
    NotificationsModal: () =>
      import("@/components/notifications/NotificationsModal"),
    WelcomeMessage: () => import("@/components/auth/WelcomeMessage"),
  },
  data: () => ({
    mapId: MAIN_MAP_ID,
    mapInitialLoad: false,
    autoDataRefresh: null,
    autoDataRefreshRateInMinutes: 15,
    notificationTimeout: 15,
    currentMapCenter: null,
    currentMapZoom: null,
    hasWatchOnly: hasWatchOnlyAccess(),
    hasSearchOnly: hasSearchOnlyAccess(),
    leftPanelMinimized: true,
  }),
  created() {
    window.addEventListener("beforeunload", this.saveBeforeExit);

    // setup some of the dynamic constants
    setTopicIcons();
    setTopicIconsSVG();
    setTopicEventCategories();
    setTopicDetailSidebarTypes();
    setTopicsIcons();
    setLabels();
  },
  async mounted() {
    this.$store.dispatch(MODULE_PREFIX_USER + GET_PREFERENCES);
    this.$store.dispatch(MODULE_PREFIX_USER + GET_WAITING_ON_WELCOME_MESSAGE);
    this.setSelectedDates();

    this.startAutoDataRefresh();

    this.openDefaultPanel();
  },
  computed: {
    ...mapGetters({
      preferences: MODULE_PREFIX_USER + "preferences",
      waitingOnWelcomeMessage: MODULE_PREFIX_USER + "waitingOnWelcomeMessage",
      refreshData: MODULE_PREFIX_MAP + "refreshData",
      selectedDates: MODULE_PREFIX_HOME + "selectedDates",
      selectorPanel: MODULE_PREFIX_HOME + "selectorPanel",
      autoRefreshEnabled: MODULE_PREFIX_HOME + "autoRefreshEnabled",
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
      firebaseEnabled: MODULE_PREFIX_HOME + "firebaseEnabled",
      topics: MODULE_PREFIX_TOPICS_V2 + "topics",
      maps: MODULE_PREFIX_MAP + "maps",
    }),
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    isWhiteLabelUserComputed() {
      return isWhiteLabelUser();
    },
  },
  methods: {
    handleMinimizedLeftSidePanel(minimized) {
      this.leftPanelMinimized = minimized;
    },
    async saveBeforeExit() {
      // this function saves the current prefs and clickstream right before the
      // user closes the browser or changes the url. It uses the fetch
      // keepalive feature to make sure the call gets made even once
      // the page has been closed.

      // TODO: see if can incorporate this logic into the current
      // clickstream and savePreferences service functions

      // send user prefs
      // const TOKEN = getAccessToken();
      // const PREFS = setupSavePreferences();
      // const BODY_PREFS = { preferences: JSON.stringify(PREFS) };
      // await httpPost({
      //   url: USER_PROFILE_GRAPHQL_URL,
      //   data: {
      //     query: updatePreferences,
      //     variables: {
      //       updatepreferencesinput: BODY_PREFS,
      //     },
      //     authMode: "OPENID_CONNECT",
      //   },
      //   keepalive: true,
      //   headers: {
      //     "Content-Type": "application/json",
      //     Accept: "application/json",
      //     Authorization: `Bearer ${TOKEN}`,
      //   },
      // });

      //TODO: This is probably overkill but we want this...
      pageTrackerLogger("Attempting to call pageTrackerSignin on page reload");
      this.pageTrackerSignout();
    },
    setSelectedDates() {
      const START_END_DATES = getDefaultStartEndDates();
      this.selectedDates.start = START_END_DATES.start;
      this.selectedDates.end = START_END_DATES.end;
      this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_SELECTED_DATES,
        this.selectedDates
      );
    },
    setAllFromPreferences() {
      this.setStabilityStateFromPreferences();
      this.setHotspotsStateFromPreferences();
      this.setTopicsStateFromPreferences();
      this.setNewsStateFromPreferences();
      this.setSocialStateFromPreferences();
      this.setPlacesStateFromPreferences();
      this.setDayNightStateFromPreferences();
      this.setPowerOutageStateFromPreferences();
    },
    refreshMapAll() {
      this.$store.dispatch(
        MODULE_PREFIX_GRIDS + GET_STABILITY_REFRESH_MAP,
        true
      );
      this.$store.dispatch(
        MODULE_PREFIX_HOTSPOTS + GET_HOTSPOTS_REFRESH_MAP,
        true
      );
      this.$store.dispatch(MODULE_PREFIX_NEWS + GET_NEWS_REFRESH_MAP, true);
      this.$store.dispatch(MODULE_PREFIX_SOCIAL + GET_SOCIAL_REFRESH_MAP, true);
      this.$store.dispatch(MODULE_PREFIX_PLACES + GET_PLACES_REFRESH_MAP, true);
      this.$store.dispatch(
        MODULE_PREFIX_DAY_NIGHT_OVERLAY + GET_DAY_NIGHT_OVERLAY_REFRESH_MAP,
        true
      );
      this.$store.dispatch(
        MODULE_PREFIX_POWER_OUTAGE + GET_POWER_OUTAGE_REFRESH_MAP,
        true
      );

      this.refreshTopics();
    },
    refreshTopics() {
      const refresh = new Date().toISOString();
      const TOPICS_ARRAY = getTopicsArray();
      TOPICS_ARRAY.forEach((topic) => {
        const field = topic.parentIds ? "apiName" : "type";
        const type = topic[field];
        if (this.topics && this.topics[type]) {
          let enabled = false;
          if (
            topic.parentIds ||
            (!topic.parentIds && topic.subtopics.length === 0)
          ) {
            enabled = this.topics[type].enabled || false;
          }
          this.$store.dispatch(
            MODULE_PREFIX_TOPICS_V2 + GET_TOPICS_V2_MAP_TOPIC,
            {
              type: type,
              enabled: enabled,
              refresh: refresh,
            }
          );
        }
      });
      this.$store.dispatch(
        MODULE_PREFIX_TOPICS_V2 + GET_TOPICS_V2_REFRESH_MAP,
        refresh
      );

      if (this.featureUnlocked("influences")) {
        const INFLUENCES_ARRAY = getInfluencesArray();
        INFLUENCES_ARRAY.forEach((topic) => {
          if (this.topics && this.topics[topic.apiName]) {
            let enabled = this.topics[topic.apiName].enabled || false;
            this.$store.dispatch(
              MODULE_PREFIX_TOPICS_V2 + GET_TOPICS_V2_MAP_TOPIC,
              {
                type: topic.apiName,
                enabled: enabled,
                refresh: refresh,
              }
            );
          }
        });
        this.$store.dispatch(
          MODULE_PREFIX_TOPICS_V2 + GET_TOPICS_V2_REFRESH_MAP,
          refresh
        );
      }
    },
    refreshSearchLocation() {
      this.$store.dispatch(
        MODULE_PREFIX_SEARCH + GET_SEARCH_LOCATION_REFRESH_MAP,
        true
      );
    },
    setPowerOutageStateFromPreferences() {
      let enabled = false;
      if (lodashGet(this.preferences, "powerOutage")) {
        enabled = lodashGet(this.preferences, "powerOutage.enabled")
          ? lodashGet(this.preferences, "powerOutage.enabled")
          : null;
      }
      this.$store.dispatch(
        MODULE_PREFIX_POWER_OUTAGE + GET_POWER_OUTAGE_REFRESH_MAP,
        enabled
      );
    },
    setStabilityStateFromPreferences() {
      let params = {};
      params.type = GRIDS_STABILITY_TYPES.country;
      params.levels = getStabilityLevelsForInitialLoad();

      if (lodashGet(this.preferences, "grids.stability")) {
        params.type = lodashGet(this.preferences, "grids.stability.type")
          ? lodashGet(this.preferences, "grids.stability.type")
          : null;
        params.levels = getStabilityLevelsFromPreferences(this.preferences);
        params.local = this.preferences.grids.stability.hasOwnProperty("local")
          ? this.preferences.grids.stability.local
          : false;
      }
      this.$store.dispatch(MODULE_PREFIX_GRIDS + GET_STABILITY_PARAMS, params);
      this.$store.dispatch(
        MODULE_PREFIX_GRIDS + GET_STABILITY_REFRESH_MAP,
        !!params.type
      );
    },
    setHotspotsStateFromPreferences() {
      let enabled = false;
      if (lodashGet(this.preferences, "grids.hotspots.enabled")) {
        enabled = true;
      }
      this.$store.dispatch(
        MODULE_PREFIX_HOTSPOTS + GET_HOTSPOTS_REFRESH_MAP,
        enabled
      );
    },
    setDayNightStateFromPreferences() {
      const ENABLED = this.preferences.dayNightLayerShow;
      this.$store.dispatch(
        MODULE_PREFIX_DAY_NIGHT_OVERLAY + GET_DAY_NIGHT_OVERLAY_REFRESH_MAP,
        ENABLED
      );
    },
    setTopicsStateFromPreferences() {
      let params = {};
      const refresh = new Date().toISOString();
      const TOPICS_ARRAY = getTopicsArray();
      TOPICS_ARRAY.forEach((topic) => {
        const field = topic.parentIds ? "apiName" : "type";
        const type = topic[field];

        params.enabled = false;
        if (
          topic.parentIds ||
          (!topic.parentIds && topic.subtopics.length === 0)
        ) {
          params.enabled = lodashGet(this.preferences, `topics.${type}.enabled`)
            ? lodashGet(this.preferences, `topics.${type}.enabled`)
            : false;
        }

        this.$store.dispatch(
          MODULE_PREFIX_TOPICS_V2 + GET_TOPICS_V2_MAP_TOPIC,
          {
            type: type,
            enabled: params.enabled,
            refresh: refresh,
          }
        );
      });
      this.$store.dispatch(
        MODULE_PREFIX_TOPICS_V2 + GET_TOPICS_V2_REFRESH_MAP,
        refresh
      );

      if (this.featureUnlocked("influences")) {
        const INFLUENCES_ARRAY = getInfluencesArray();
        INFLUENCES_ARRAY.forEach((topic) => {
          params.enabled = lodashGet(
            this.preferences,
            `topics.${topic.apiName}.enabled`
          )
            ? lodashGet(this.preferences, `topics.${topic.apiName}.enabled`)
            : false;

          this.$store.dispatch(
            MODULE_PREFIX_TOPICS_V2 + GET_TOPICS_V2_MAP_TOPIC,
            {
              type: topic.apiName,
              enabled: params.enabled,
              refresh: refresh,
            }
          );
        });
        this.$store.dispatch(
          MODULE_PREFIX_TOPICS_V2 + GET_TOPICS_V2_REFRESH_MAP,
          refresh
        );
      }
    },
    setNewsStateFromPreferences() {
      const ENABLED = lodashGet(this.preferences, "media.news.enabled")
        ? lodashGet(this.preferences, "media.news.enabled")
        : false;

      this.$store.dispatch(MODULE_PREFIX_NEWS + GET_NEWS_REFRESH_MAP, ENABLED);
    },
    setSocialStateFromPreferences() {
      const ENABLED = lodashGet(this.preferences, "media.social.enabled")
        ? lodashGet(this.preferences, "media.social.enabled")
        : false;

      this.$store.dispatch(
        MODULE_PREFIX_SOCIAL + GET_SOCIAL_REFRESH_MAP,
        ENABLED
      );
    },
    setPlacesStateFromPreferences() {
      const PLACES = lodashGet(this.preferences, "places");
      let exists = false;
      if (PLACES) {
        PLACES.forEach((place) => {
          if (place.mapped) {
            exists = true;
          }
        });
      }

      this.$store.dispatch(
        MODULE_PREFIX_PLACES + GET_PLACES_REFRESH_MAP,
        exists
      );
    },
    startAutoDataRefresh() {
      console.log("Starting ALL DATA auto refresh");
      this.autoDataRefresh = setInterval(() => {
        this.refreshMapAll();
        this.$store.dispatch(MODULE_PREFIX_MAP + GET_MAP_REFRESH_DATA, false);
        console.log("ALL DATA auto refreshed", new Date());
      }, this.autoDataRefreshRateInMinutes * 60 * 1000);
    },
    stopAutoDataRefresh() {
      if (this.autoDataRefresh) {
        console.log("Stopping ALL DATA auto refresh");
        clearInterval(this.autoDataRefresh);
        this.autoDataRefresh = null;
      }
    },
    async openGrdHome() {
      if (this.selectorPanel.selected === DETAIL_SIDEBAR_TYPES.grdHome) {
        this.selectorPanel.selected = null;
        this.selectorPanel.visible = false;
        this.selectorPanel.isSelected = false;
        await this.$store.dispatch(
          MODULE_PREFIX_HOME + GET_SELECTOR_PANEL,
          this.selectorPanel
        );
      }
      this.selectorPanel.selected = DETAIL_SIDEBAR_TYPES.grdHome;
      this.selectorPanel.visible = true;
      this.selectorPanel.isSelected = true;
      await this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_SELECTOR_PANEL,
        this.selectorPanel
      );
    },
    async openGrdAboutUs() {
      if (this.selectorPanel.selected === DETAIL_SIDEBAR_TYPES.grdAboutUs) {
        this.selectorPanel.selected = null;
        this.selectorPanel.visible = false;
        this.selectorPanel.isSelected = false;
        await this.$store.dispatch(
          MODULE_PREFIX_HOME + GET_SELECTOR_PANEL,
          this.selectorPanel
        );
      }
      this.selectorPanel.selected = DETAIL_SIDEBAR_TYPES.grdAboutUs;
      this.selectorPanel.visible = true;
      this.selectorPanel.isSelected = true;
      await this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_SELECTOR_PANEL,
        this.selectorPanel
      );
    },
    async openGrdReporting() {
      if (this.selectorPanel.selected === DETAIL_SIDEBAR_TYPES.grdReporting) {
        this.selectorPanel.selected = null;
        this.selectorPanel.visible = false;
        this.selectorPanel.isSelected = false;
        await this.$store.dispatch(
          MODULE_PREFIX_HOME + GET_SELECTOR_PANEL,
          this.selectorPanel
        );
      }
      this.selectorPanel.selected = DETAIL_SIDEBAR_TYPES.grdReporting;
      this.selectorPanel.visible = true;
      this.selectorPanel.isSelected = true;
      await this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_SELECTOR_PANEL,
        this.selectorPanel
      );
    },
    async openGrdCountries() {
      if (this.selectorPanel.selected === DETAIL_SIDEBAR_TYPES.grdCountries) {
        this.selectorPanel.selected = null;
        this.selectorPanel.visible = false;
        this.selectorPanel.isSelected = false;
        await this.$store.dispatch(
          MODULE_PREFIX_HOME + GET_SELECTOR_PANEL,
          this.selectorPanel
        );
      }
      this.selectorPanel.selected = DETAIL_SIDEBAR_TYPES.grdCountries;
      this.selectorPanel.visible = true;
      this.selectorPanel.isSelected = true;
      await this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_SELECTOR_PANEL,
        this.selectorPanel
      );
    },
    pageTrackerSignout() {
      pageTrackerLogger("_pageTrackerSignout");
      const profile = JSON.parse(localStorage.getItem("user_profile"));
      if (profile && profile.email) {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.system,
          EVENT_ACTIONS.loggedOut,
          profile.email
        );
      }
    },
    async openDefaultPanel() {
      if (this.waitingOnWelcomeMessage) {
        //Don't open anything it'll cover the welcome message
      } else if (this.$route.name === "grd-aboutus") {
        await this.openGrdAboutUs();
      } else if (this.$route.name === "grd-countries") {
        await this.openGrdCountries();
      } else if (this.$route.name === "grd-reporting") {
        await this.openGrdReporting();
      } else if (
        this.$route.name === "grd" ||
        getPreferredLandingPage() === "grd"
      ) {
        await this.openGrdHome();
      }
    },
  },
  watch: {
    refreshData: {
      handler() {
        if (this.refreshData) {
          this.refreshMapAll();
          this.refreshSearchLocation();
          this.$store.dispatch(MODULE_PREFIX_MAP + GET_MAP_REFRESH_DATA, false);
        }
      },
    },
    autoRefreshEnabled: {
      handler() {
        if (this.autoRefreshEnabled) {
          this.startAutoDataRefresh();
        } else {
          this.stopAutoDataRefresh();
        }
      },
    },
    firebaseEnabled: {
      handler(enabled) {
        if (!enabled) {
          this.stopAutoDataRefresh();
        }
      },
    },
    waitingOnWelcomeMessage: {
      handler(val, oldVal) {
        if (!val && val !== oldVal && !val) {
          this.openDefaultPanel();
        }
      },
    },
  },
  beforeDestroy() {
    this.stopAutoDataRefresh();
  },
};
</script>

<style scoped>
.hyp-container-home {
  background-color: #f7f7fa;
  height: 100%;
  width: 100%;
}
.hyp-home-right-container {
  float: left;
}
.home-info-viewer {
  width: fit-content;
  float: right;
  margin-left: 8px;
}
.xs1-overrides {
  max-width: fit-content !important;
  padding: 4px 0 !important;
  pointer-events: auto;
}
</style>

<style lang="scss">
.hyp-footer {
  bottom: 0;
  position: absolute;
  z-index: 300;
  width: 100%;
  background-color: #2a2a2a;
  color: white;
  font-size: 12px;
  padding: 5px;
}
</style>
